/**
 * Reklamationen (Fachmodul "complaints").
 *
 * Liste der Vorgänge mit Statusfilter, Erfassung und Bearbeitung im
 * Dialog. Eine Reklamation ist ein Vorgang, kein Beleg: Sie hat einen
 * Status statt eines Festschreibe-Lebenslaufs. Der Warenrücklauf wird als
 * Lagerbewegung gebucht, die Gutschrift läuft über den normalen
 * Belegablauf.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  COMPLAINT_RESOLUTION_LABELS,
  COMPLAINT_STATUS_LABELS,
  COMPLAINT_STATUSES,
  formatDate,
  formatQuantity,
  getDocumentType,
  type ComplaintResolution,
  type ComplaintStatus,
  type DocumentType,
} from '@erp/shared';

import { ApiError, api, withQuery } from '../api/client';
import type { ListResponse, Partner } from '../api/types';
import { useAuth } from '../auth';
import { ItemPicker } from '../components/ItemPicker';
import { SearchSelect } from '../components/SearchSelect';
import { PageHeader } from '../components/Layout';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Dialog,
  EmptyState,
  Field,
  LoadingBlock,
  Select,
  TextArea,
  TextInput,
} from '../components/ui';

type ComplaintRow = {
  id: string;
  number: string;
  partnerId: string;
  partnerName: string;
  documentId: string | null;
  itemId: string | null;
  itemName: string | null;
  quantity: number | null;
  receivedDate: string;
  reason: string;
  status: ComplaintStatus;
  resolutionType: ComplaintResolution | null;
  returnBooked: boolean;
};

type ComplaintDetail = {
  complaint: {
    id: string;
    number: string;
    partnerId: string;
    documentId: string | null;
    itemId: string | null;
    quantity: number | null;
    receivedDate: string;
    reason: string;
    status: ComplaintStatus;
    resolutionType: ComplaintResolution | null;
    resolutionNote: string | null;
    resolvedAt: string | null;
    returnBooked: boolean;
  };
  partner: { id: string; name: string; number: string } | null;
  item: { id: string; name: string; number: string; unit: string } | null;
  document: { id: string; type: string; number: string | null } | null;
};

const DOCUMENT_PATHS: Record<string, string> = {
  quote: '/verkauf/angebote',
  sales_order: '/verkauf/auftraege',
  delivery_note: '/verkauf/lieferscheine',
  invoice: '/verkauf/rechnungen',
  credit_note: '/verkauf/gutschriften',
};

function statusBadge(status: ComplaintStatus) {
  const kind =
    status === 'open'
      ? 'warning'
      : status === 'in_progress'
        ? 'accent'
        : status === 'resolved'
          ? 'success'
          : 'danger';
  return <Badge kind={kind}>{COMPLAINT_STATUS_LABELS[status]}</Badge>;
}

export function ComplaintsPage() {
  const { can } = useAuth();
  const [statusFilter, setStatusFilter] = useState<ComplaintStatus | ''>('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['complaints', statusFilter, search],
    queryFn: () =>
      api.get<ListResponse<ComplaintRow>>(
        withQuery('/api/complaints', { status: statusFilter, q: search, limit: 100 }),
      ),
  });

  return (
    <>
      <PageHeader
        title="Reklamationen"
        actions={
          can('complaints:write') ? (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Reklamation erfassen
            </button>
          ) : undefined
        }
      />

      <div className="row" style={{ gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`btn btn-sm${statusFilter === '' ? ' btn-primary' : ''}`}
          onClick={() => setStatusFilter('')}
        >
          Alle
        </button>
        {COMPLAINT_STATUSES.map((status) => (
          <button
            key={status}
            type="button"
            className={`btn btn-sm${statusFilter === status ? ' btn-primary' : ''}`}
            onClick={() => setStatusFilter(status)}
          >
            {COMPLAINT_STATUS_LABELS[status]}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <TextInput value={search} onChange={setSearch} placeholder="Nummer, Kunde, Grund …" />
      </div>

      <div className="card">
        {isLoading || !data ? (
          <LoadingBlock />
        ) : data.items.length === 0 ? (
          <EmptyState
            title="Keine Reklamationen gefunden."
            hint={'Neue Vorgänge werden über „Reklamation erfassen" angelegt.'}
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Nummer</th>
                  <th>Kunde</th>
                  <th>Artikel</th>
                  <th>Beanstandung</th>
                  <th className="shrink">Eingang</th>
                  <th className="shrink">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setOpenId(row.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="mono">{row.number}</td>
                    <td>{row.partnerName}</td>
                    <td>
                      {row.itemName ?? <span className="faint">—</span>}
                      {row.quantity ? (
                        <span className="faint"> ({formatQuantity(row.quantity)})</span>
                      ) : null}
                    </td>
                    <td
                      style={{
                        maxWidth: 340,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.reason}
                    </td>
                    <td>{formatDate(row.receivedDate)}</td>
                    <td>{statusBadge(row.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating ? (
        <CreateComplaintDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setOpenId(id);
          }}
        />
      ) : null}
      {openId ? <ComplaintDetailDialog id={openId} onClose={() => setOpenId(null)} /> : null}
    </>
  );
}

// --- Erfassung ---------------------------------------------------------------

function CreateComplaintDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [partnerId, setPartnerId] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [itemId, setItemId] = useState<string | null>(null);
  const [itemLabel, setItemLabel] = useState('');
  const [quantityText, setQuantityText] = useState('');
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: customers } = useQuery({
    queryKey: ['partners', 'customer', 'all'],
    queryFn: () =>
      api.get<ListResponse<Partner>>(withQuery('/api/partners', { role: 'customer', limit: 500 })),
  });

  // Belege des gewaehlten Kunden fuer den optionalen Belegbezug.
  const { data: partnerDocuments } = useQuery({
    queryKey: ['partner-documents', partnerId],
    queryFn: () =>
      api.get<{ items: { id: string; type: string; number: string | null }[] }>(
        withQuery('/api/documents', { partnerId, limit: 50, sort: 'documentDate', dir: 'desc' }),
      ),
    enabled: partnerId !== '',
  });

  const create = useMutation({
    mutationFn: () => {
      const quantity = quantityText.trim()
        ? Math.round(Number(quantityText.replace(',', '.')) * 1000)
        : null;
      return api.post<{ complaint: { id: string } }>('/api/complaints', {
        partnerId,
        documentId: documentId || null,
        itemId,
        quantity,
        receivedDate,
        reason,
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['complaints'] });
      toast.success('Die Reklamation wurde erfasst.');
      onCreated(result.complaint.id);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Beim Speichern ist ein Fehler aufgetreten.');
      }
    },
  });

  const numberedDocuments = (partnerDocuments?.items ?? []).filter((entry) => entry.number);

  return (
    <Dialog
      title="Reklamation erfassen"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={create.isPending || !partnerId || !reason.trim()}
            onClick={() => create.mutate()}
          >
            Erfassen
          </button>
        </>
      }
    >
      <Field label="Kunde" required error={errors.partnerId}>
        <SearchSelect
          value={partnerId}
          onChange={(value) => {
            setPartnerId(value);
            setDocumentId('');
          }}
          options={(customers?.items ?? []).map((partner) => ({
            value: partner.id,
            label: `${partner.number} · ${partner.name}`,
          }))}
          placeholder="Kundenname oder -nummer tippen …"
          invalid={Boolean(errors.partnerId)}
        />
      </Field>

      <Field label="Bezogener Beleg" error={errors.documentId} hint="Optional, z. B. die Rechnung oder der Lieferschein">
        <SearchSelect
          value={documentId}
          onChange={setDocumentId}
          options={numberedDocuments.map((entry) => ({
            value: entry.id,
            label: `${getDocumentType(entry.type as DocumentType).label} ${entry.number}`,
          }))}
          placeholder={partnerId ? 'Belegnummer tippen …' : 'Zuerst Kunde wählen'}
          emptyLabel="Kein Belegbezug"
          disabled={!partnerId}
        />
      </Field>

      <Field label="Betroffener Artikel" error={errors.itemId} hint="Optional">
        <ItemPicker
          value={itemLabel}
          onSelect={(item) => {
            setItemId(item.id);
            setItemLabel(`${item.number} · ${item.name}`);
          }}
        />
      </Field>

      <div className="row" style={{ gap: 12 }}>
        <Field label="Betroffene Menge" error={errors.quantity}>
          <TextInput value={quantityText} onChange={setQuantityText} placeholder="z. B. 2" />
        </Field>
        <Field label="Eingangsdatum" required error={errors.receivedDate}>
          <TextInput type="date" value={receivedDate} onChange={setReceivedDate} />
        </Field>
      </div>

      <Field label="Beanstandung" required error={errors.reason}>
        <TextArea value={reason} onChange={setReason} rows={4} />
      </Field>
    </Dialog>
  );
}

// --- Detail ------------------------------------------------------------------

function ComplaintDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const readOnly = !can('complaints:write');

  const [closing, setClosing] = useState<'resolved' | 'rejected' | null>(null);
  const [resolutionType, setResolutionType] = useState<ComplaintResolution | ''>('');
  const [resolutionNote, setResolutionNote] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['complaint', id],
    queryFn: () => api.get<ComplaintDetail>(`/api/complaints/${id}`),
  });

  function mutationHandlers(message: string) {
    return {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['complaints'] });
        queryClient.invalidateQueries({ queryKey: ['complaint', id] });
        setClosing(null);
        toast.success(message);
      },
      onError: (error: unknown) =>
        toast.error(error instanceof ApiError ? error.message : 'Die Aktion ist fehlgeschlagen.'),
    };
  }

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/api/complaints/${id}`, body),
    ...mutationHandlers('Der Vorgang wurde aktualisiert.'),
  });

  const bookReturn = useMutation({
    mutationFn: () =>
      api.post(`/api/complaints/${id}/return`, {
        movementDate: new Date().toISOString().slice(0, 10),
      }),
    ...mutationHandlers('Der Rücklauf wurde als Lagerzugang gebucht.'),
  });

  if (isLoading || !data) {
    return (
      <Dialog title="Reklamation" onClose={onClose}>
        <LoadingBlock />
      </Dialog>
    );
  }

  const { complaint, partner, item, document } = data;
  const closed = complaint.status === 'resolved' || complaint.status === 'rejected';

  return (
    <Dialog title={`Reklamation ${complaint.number}`} onClose={onClose} wide>
      <div className="row" style={{ gap: 8, marginBottom: 10, alignItems: 'center' }}>
        {statusBadge(complaint.status)}
        {complaint.resolutionType ? (
          <span className="faint">
            Erledigung: {COMPLAINT_RESOLUTION_LABELS[complaint.resolutionType]}
          </span>
        ) : null}
        {complaint.returnBooked ? <Badge kind="accent">Rücklauf gebucht</Badge> : null}
      </div>

      <div className="table-wrap" style={{ marginBottom: 12 }}>
        <table className="data">
          <tbody>
            <tr>
              <td className="faint" style={{ width: '11em' }}>Kunde</td>
              <td>
                {partner ? (
                  <Link to={`/kunden/${partner.id}`}>{partner.number} · {partner.name}</Link>
                ) : '—'}
              </td>
            </tr>
            <tr>
              <td className="faint">Bezogener Beleg</td>
              <td>
                {document && document.number ? (
                  DOCUMENT_PATHS[document.type] ? (
                    <Link to={`${DOCUMENT_PATHS[document.type]}/${document.id}`}>
                      {getDocumentType(document.type as DocumentType).label} {document.number}
                    </Link>
                  ) : (
                    `${getDocumentType(document.type as DocumentType).label} ${document.number}`
                  )
                ) : (
                  <span className="faint">—</span>
                )}
              </td>
            </tr>
            <tr>
              <td className="faint">Artikel</td>
              <td>
                {item ? `${item.number} · ${item.name}` : <span className="faint">—</span>}
                {complaint.quantity ? ` — ${formatQuantity(complaint.quantity)}` : null}
              </td>
            </tr>
            <tr>
              <td className="faint">Eingang</td>
              <td>{formatDate(complaint.receivedDate)}</td>
            </tr>
            <tr>
              <td className="faint">Beanstandung</td>
              <td style={{ whiteSpace: 'pre-wrap' }}>{complaint.reason}</td>
            </tr>
            {complaint.resolutionNote ? (
              <tr>
                <td className="faint">Erledigungsvermerk</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>{complaint.resolutionNote}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {!readOnly && closing ? (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          {closing === 'resolved' ? (
            <Field label="Wie wurde die Reklamation erledigt?" required>
              <Select
                value={resolutionType}
                onChange={(value) => setResolutionType(value)}
                options={Object.entries(COMPLAINT_RESOLUTION_LABELS).map(([value, label]) => ({
                  value: value as ComplaintResolution,
                  label,
                }))}
                placeholder="Erledigungsart wählen …"
              />
            </Field>
          ) : (
            <Alert kind="warning">
              Die Reklamation wird als unberechtigt abgelehnt. Der Vermerk hilft bei Rückfragen.
            </Alert>
          )}
          <Field label="Vermerk">
            <TextArea value={resolutionNote} onChange={setResolutionNote} rows={2} />
          </Field>
          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-sm" onClick={() => setClosing(null)}>
              Abbrechen
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={patch.isPending || (closing === 'resolved' && !resolutionType)}
              onClick={() =>
                patch.mutate({
                  status: closing,
                  ...(closing === 'resolved' ? { resolutionType } : {}),
                  resolutionNote: resolutionNote || null,
                })
              }
            >
              {closing === 'resolved' ? 'Als erledigt abschließen' : 'Ablehnen'}
            </button>
          </div>
        </div>
      ) : null}

      {!readOnly && !closing ? (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {complaint.status === 'open' ? (
            <button
              type="button"
              className="btn btn-sm"
              disabled={patch.isPending}
              onClick={() => patch.mutate({ status: 'in_progress' })}
            >
              In Bearbeitung nehmen
            </button>
          ) : null}
          {!closed ? (
            <>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setResolutionType('');
                  setResolutionNote('');
                  setClosing('resolved');
                }}
              >
                Abschließen …
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setResolutionNote('');
                  setClosing('rejected');
                }}
              >
                Ablehnen …
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-sm"
              disabled={patch.isPending}
              onClick={() => patch.mutate({ status: 'in_progress' })}
            >
              Wieder öffnen
            </button>
          )}
          {complaint.itemId && complaint.quantity && !complaint.returnBooked ? (
            <button
              type="button"
              className="btn btn-sm"
              disabled={bookReturn.isPending}
              onClick={() => bookReturn.mutate()}
              title="Bucht die betroffene Menge als Zugang in das Vorgabelager"
            >
              Rücklauf buchen
            </button>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
